/**
 * MultiTraderWorkspace — manages multiple trader panels in a grid layout.
 *
 * Allows users to open concurrent trading windows for different asset classes
 * (Equity, FX, Bonds, ETF). Each panel operates independently with its own
 * state but shares the same BrokerClient and WsClient for API calls.
 *
 * Architecture:
 *   - Tracks open traders in state: openTraders = [{id, type}, ...]
 *   - Renders TraderPanel components in a responsive grid (2x2, 1x3, etc.)
 *   - "Open New Trader" button shows menu to select asset class
 *   - Panel close events remove trader from list
 *   - Passes shared BrokerClient + WsClient to each panel
 *   - State-managed to allow quick open/close/swap of panels
 *
 * Mobile Features:
 *   - Vertical stacking at <768px breakpoint (1 column)
 *   - Touch-friendly close buttons (40px hit area)
 *   - Swipe/arrow navigation between traders
 *   - Trader index indicator: "Equity Trader #1 (1/4)"
 *   - Tap trader name to see all traders (modal list)
 *   - Auto-hide top bar on scroll for more content space
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useAuth } from '../auth/AuthContext';
import type { BrokerClient } from '../api/brokerClient';
import type { WsClient } from '../api/wsClient';
import { TraderPanel } from './TraderPanel';
import { TopBar } from './TopBar';
import { NewsTicker } from './NewsTicker';
import { BROKERS, SOR_BROKER, type BrokerId } from '../lib/brokers';
import { openDetachedWindow } from '../utils/windowManager';
import { WindowCommunicationManager } from '../utils/windowCommunication';
import { useDetachedWindowResize } from '../hooks/useDetachedWindowResize';
import { useRestoreDetachedWindowPosition } from '../hooks/useRestoreDetachedWindowPosition';

// Type for trader navigation handlers
interface TraderNavHandlers {
  handleNav: (k: 'Dashboard' | 'Markets' | 'Portfolio' | 'Orders') => void;
}

/**
 * Type of trader (asset class) that can be opened
 */
export type TraderType = 'equity' | 'fx' | 'bonds' | 'etf';

/**
 * Represents an open trader window
 */
export interface OpenTrader {
  id: string;
  type: TraderType;
}

export interface MultiTraderWorkspaceProps {
  brokerClient: BrokerClient;
  wsClient: WsClient;
  onExit?: () => void;
}

/**
 * Generate a unique trader ID
 */
function generateTraderId(): string {
  return `trader-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
}

/**
 * Determine grid layout class based on number of traders
 */
function getGridLayout(count: number): string {
  if (count === 1) return 'grid-1x1';
  if (count === 2) return 'grid-1x2';
  if (count === 3) return 'grid-1x3';
  if (count === 4) return 'grid-2x2';
  if (count === 5) return 'grid-2x3'; // 6 cells
  if (count === 6) return 'grid-2x3';
  return 'grid-3x3'; // 7+ traders: 3x3
}

/**
 * Get display label for trader type
 */
function getTraderTypeLabel(type: TraderType): string {
  return {
    equity: 'Equity Trader',
    fx: 'FX Trader',
    bonds: 'Bond Trader',
    etf: 'ETF Trader',
  }[type];
}

export function MultiTraderWorkspace({
  brokerClient,
  wsClient,
  onExit,
}: MultiTraderWorkspaceProps) {
  const auth = useAuth();
  const [openTraders, setOpenTraders] = useState<OpenTrader[]>([]);
  const [detachedTraders, setDetachedTraders] = useState<Set<string>>(new Set());
  const [menuOpen, setMenuOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [currentTraderIndex, setCurrentTraderIndex] = useState(0);
  const [showTraderList, setShowTraderList] = useState(false);
  const [activeNav, setActiveNav] = useState<'Dashboard' | 'Markets' | 'Portfolio' | 'Orders'>('Dashboard');
  const traderNavHandlers = useRef<Map<string, TraderNavHandlers>>(new Map());
  const [topBarHidden, setTopBarHidden] = useState(false);
  const [toastMessage, setToastMessage] = useState<string | null>(null);
  const [activeBroker, setActiveBroker] = useState<BrokerId>('sor');
  const [marketTime, setMarketTime] = useState('14:30 WAT');
  const [asiPrice, setAsiPrice] = useState(110245.67);
  const [asiPct, setAsiPct] = useState(0.85);
  const [dangcemPrice, setDangcemPrice] = useState(322.28);
  const [dangcemPct, setDangcemPct] = useState(-14.15);
  const gridRef = useRef<HTMLDivElement>(null);
  const touchStartXRef = useRef(0);
  const commManagerRef = useRef<WindowCommunicationManager | null>(null);

  // Enable window resize/move tracking for detached windows
  useDetachedWindowResize();
  useRestoreDetachedWindowPosition();

  /**
   * Initialize communication manager for detached windows
   */
  useEffect(() => {
    commManagerRef.current = new WindowCommunicationManager(false); // isDetached = false (main window)

    // Listen for window close events from detached windows
    commManagerRef.current.on('window:close', (msg) => {
      if (msg.traderId && msg.payload?.reattach) {
        // Window was reattached, do nothing (trader stays in list)
      } else if (msg.traderId) {
        // Window was closed, remove from detached set
        setDetachedTraders((prev) => {
          const next = new Set(prev);
          next.delete(msg.traderId!);
          return next;
        });
      }
    });

    return () => {
      commManagerRef.current?.destroy();
    };
  }, []);

  /**
   * Open a new trader of the given type
   */
  const openTrader = useCallback((type: TraderType) => {
    const newTrader: OpenTrader = {
      id: generateTraderId(),
      type,
    };
    setOpenTraders((prev) => [...prev, newTrader]);
    setMenuOpen(false);
  }, []);

  /**
   * Close a trader by ID
   */
  const closeTrader = useCallback((id: string) => {
    setOpenTraders((prev) => prev.filter((t) => t.id !== id));
    // Also remove from detached set if present
    setDetachedTraders((prev) => {
      const next = new Set(prev);
      next.delete(id);
      return next;
    });
  }, []);

  /**
   * Show a toast notification
   */
  const showToast = useCallback((message: string, type: 'success' | 'error' | 'info' = 'info') => {
    setToastMessage(message);
    setTimeout(() => setToastMessage(null), 3000);
  }, []);

  /**
   * Handle detach/pop-out of a trader to a new window
   */
  const handleDetachTrader = useCallback((id: string, type: TraderType) => {
    const trader = openTraders.find((t) => t.id === id);
    if (!trader) return;

    try {
      // Mark trader as detached in this window
      setDetachedTraders((prev) => new Set(prev).add(id));

      // Use windowManager to open detached window
      // Default position: center of screen with 800x600 size
      const x = window.innerWidth / 2 - 400;
      const y = window.innerHeight / 2 - 300;

      const windowInfo = openDetachedWindow(id, type, x, y, 800, 600);

      if (windowInfo?.windowRef) {
        // Show success toast
        setToastMessage(`${type.charAt(0).toUpperCase() + type.slice(1)} trader opened in new window`);

        // Register window with communication manager
        if (commManagerRef.current && windowInfo.windowRef) {
          commManagerRef.current.registerChildWindow(id, windowInfo.windowRef);
        }

        setTimeout(() => setToastMessage(null), 3000);
      } else {
        // Pop-ups blocked or failed to open
        setToastMessage('Pop-up blocked by browser. Please allow pop-ups for this site.');
        setDetachedTraders((prev) => {
          const next = new Set(prev);
          next.delete(id);
          return next;
        });
        setTimeout(() => setToastMessage(null), 3000);
      }
    } catch (err) {
      console.error('Failed to detach trader:', err);
      setToastMessage('Failed to open trader in new window');
      setDetachedTraders((prev) => {
        const next = new Set(prev);
        next.delete(id);
        return next;
      });
      setTimeout(() => setToastMessage(null), 3000);
    }
  }, [openTraders]);

  /**
   * Close all traders
   */
  const closeAll = useCallback(() => {
    setOpenTraders([]);
  }, []);

  /**
   * Register a trader's navigation handler
   */
  const registerTraderNavHandler = useCallback((traderId: string, handlers: TraderNavHandlers) => {
    traderNavHandlers.current.set(traderId, handlers);
  }, []);

  /**
   * Handle navigation on the active trader panel
   */
  const handleTopBarNav = useCallback((k: 'Dashboard' | 'Markets' | 'Portfolio' | 'Orders') => {
    setActiveNav(k);
    // Get the active trader ID
    const activeTrader = openTraders[currentTraderIndex];
    if (!activeTrader) return;

    // Call the active trader's navigation handler
    const handlers = traderNavHandlers.current.get(activeTrader.id);
    if (handlers) {
      handlers.handleNav(k);
    }
  }, [openTraders, currentTraderIndex]);

  const gridLayoutClass = useMemo(() => getGridLayout(openTraders.length), [openTraders.length]);

  /**
   * Route-aware initialization: automatically open the appropriate trader
   * based on the current pathname on component mount.
   *   - /trader → opens Equity trader by default
   *   - /fx-trader → opens FX trader by default
   *   - /trader?detached=true&traderId=xxx&type=equity → opens specific detached trader
   */
  useEffect(() => {
    if (openTraders.length === 0) {
      const path = window.location.pathname;
      const params = new URLSearchParams(window.location.search);

      // Check if this is a detached window with specific trader request
      if (params.get('detached') === 'true') {
        const traderId = params.get('traderId');
        const traderType = params.get('type') as TraderType | null;

        if (traderId && traderType && ['equity', 'fx', 'bonds', 'etf'].includes(traderType)) {
          // Open the specific detached trader
          const newTrader: OpenTrader = { id: traderId, type: traderType };
          setOpenTraders([newTrader]);
          setCurrentTraderIndex(0);
          return;
        }
      }

      // Default behavior: open based on pathname
      if (path === '/trader' || path.startsWith('/trader/')) {
        openTrader('equity');
      } else if (path === '/fx-trader' || path.startsWith('/fx-trader/')) {
        openTrader('fx');
      }
    }
  }, []); // Only run once on mount

  /**
   * Handle window resize for mobile detection
   */
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  /**
   * Update market time ticker and live prices
   */
  useEffect(() => {
    const id = setInterval(() => {
      // Update market time
      const d = new Date();
      setMarketTime(`${String(d.getUTCHours() + 1).padStart(2, '0')}:${String(d.getUTCMinutes()).padStart(2, '0')} WAT`);

      // Update NGX-ASI price (random walk with slight bias)
      setAsiPrice((prev) => {
        const change = (Math.random() - 0.48) * 500; // Slight upward bias
        return Math.max(100000, prev + change);
      });
      setAsiPct((prev) => (Math.random() - 0.5) * 2);

      // Update DANGCEM price (volatile)
      setDangcemPrice((prev) => {
        const change = (Math.random() - 0.5) * 5;
        return Math.max(100, prev + change);
      });
      setDangcemPct((prev) => (Math.random() - 0.5) * 3);
    }, 1000);
    return () => clearInterval(id);
  }, []);

  /**
   * Clamp trader index within bounds
   */
  const clampedIndex = Math.min(currentTraderIndex, Math.max(0, openTraders.length - 1));

  /**
   * Navigate to next trader
   */
  const goToNextTrader = useCallback(() => {
    setCurrentTraderIndex((prev) => Math.min(prev + 1, openTraders.length - 1));
  }, [openTraders.length]);

  /**
   * Navigate to previous trader
   */
  const goToPrevTrader = useCallback(() => {
    setCurrentTraderIndex((prev) => Math.max(prev - 1, 0));
  }, []);

  /**
   * Jump to specific trader in the list
   */
  const goToTrader = useCallback((index: number) => {
    setCurrentTraderIndex(Math.max(0, Math.min(index, openTraders.length - 1)));
    setShowTraderList(false);
  }, [openTraders.length]);

  /**
   * Handle touch start for swipe detection
   */
  const handleTouchStart = (e: React.TouchEvent) => {
    touchStartXRef.current = e.touches[0]?.clientX ?? 0;
  };

  /**
   * Handle touch end for swipe gestures (left/right to navigate)
   */
  const handleTouchEnd = (e: React.TouchEvent) => {
    if (!isMobile || openTraders.length <= 1) return;

    const touchEndX = e.changedTouches[0]?.clientX ?? 0;
    const diff = touchStartXRef.current - touchEndX;

    if (Math.abs(diff) > 50) {
      if (diff > 0) {
        goToNextTrader();
      } else {
        goToPrevTrader();
      }
    }
  };

  /**
   * Handle scroll to hide/show top bar on mobile, with logging for debugging
   */
  useEffect(() => {
    if (!gridRef.current) return;

    let lastScrollTop = 0;
    const handleScroll = (e: Event) => {
      const grid = e.target as HTMLDivElement;
      const scrollTop = grid.scrollTop;
      const scrollHeight = grid.scrollHeight;
      const clientHeight = grid.clientHeight;

      // Log scroll events for debugging
      console.log('Grid scroll event:', {
        scrollTop,
        scrollHeight,
        clientHeight,
        scrollableVertically: scrollHeight > clientHeight,
        scrollableHorizontally: grid.scrollWidth > grid.clientWidth,
        scrollWidth: grid.scrollWidth,
        clientWidth: grid.clientWidth,
      });

      // Mobile: hide top bar when scrolling down
      if (isMobile) {
        setTopBarHidden(scrollTop > lastScrollTop && scrollTop > 50);
      }
      lastScrollTop = scrollTop;
    };

    const grid = gridRef.current;

    // Log initial grid dimensions
    console.log('Grid initialized with dimensions:', {
      scrollHeight: grid.scrollHeight,
      scrollWidth: grid.scrollWidth,
      clientHeight: grid.clientHeight,
      clientWidth: grid.clientWidth,
      scrollable: {
        vertical: grid.scrollHeight > grid.clientHeight,
        horizontal: grid.scrollWidth > grid.clientWidth,
      },
    });

    grid.addEventListener('scroll', handleScroll, { passive: true });
    return () => grid.removeEventListener('scroll', handleScroll);
  }, [isMobile]);

  return (
    <div className="multi-trader-workspace">
      {/* Global TopBar - always visible at top with live market data */}
      <TopBar
        marketStatus="OPEN"
        marketTime={marketTime}
        tickers={[
          { sym: 'NGX-ASI', px: asiPrice.toLocaleString('en-NG', { maximumFractionDigits: 2 }), pct: asiPct },
          { sym: 'DANGCEM', px: dangcemPrice.toFixed(2), pct: dangcemPct },
        ]}
        wsStatus="open"
        mfaSatisfied={auth.mfaSatisfied}
        username={auth.username}
        onSignOut={auth.signOut}
        activeBroker={activeBroker}
        onChangeBroker={(id) => setActiveBroker(id as BrokerId)}
        brokerOptions={BROKERS.map((b) => ({ id: b.id, label: b.label, glyph: b.logoGlyph }))}
        activeNav={activeNav}
        onNav={handleTopBarNav}
      />

      {/* News Ticker - above workspace header */}
      <NewsTicker />

      {/* Workspace Header bar with controls (auto-hides on mobile scroll) */}
      <div className={`workspace-header ${topBarHidden ? 'hidden' : ''}`} style={{ marginTop: '0px' }}>
        <div className="workspace-header-title">
          <h1>{isMobile && openTraders.length > 0 ? `Trader ${clampedIndex + 1}/${openTraders.length}` : 'Multi-Trader Workspace'}</h1>
          <span className="trader-count">
            {openTraders.length} {openTraders.length === 1 ? 'panel' : 'panels'} open
          </span>
        </div>

        <div className="workspace-header-actions">
          {/* Open New Trader button with dropdown menu */}
          <div className="open-trader-menu">
            <button
              type="button"
              className="btn btn-primary open-trader-btn"
              onClick={() => setMenuOpen(!menuOpen)}
              title="Open a new trader window"
            >
              + Open New Trader
            </button>

            {menuOpen && (
              <div className="open-trader-dropdown" role="menu">
                <button
                  type="button"
                  className="dropdown-item"
                  onClick={() => openTrader('equity')}
                  role="menuitem"
                >
                  <span className="icon">📈</span>
                  <span>Equity Trader</span>
                </button>
                <button
                  type="button"
                  className="dropdown-item"
                  onClick={() => openTrader('fx')}
                  role="menuitem"
                >
                  <span className="icon">💱</span>
                  <span>FX Trader</span>
                </button>
                <button
                  type="button"
                  className="dropdown-item"
                  onClick={() => openTrader('bonds')}
                  role="menuitem"
                >
                  <span className="icon">📋</span>
                  <span>Bond Trader</span>
                </button>
                <button
                  type="button"
                  className="dropdown-item"
                  onClick={() => openTrader('etf')}
                  role="menuitem"
                >
                  <span className="icon">🎯</span>
                  <span>ETF Trader</span>
                </button>
              </div>
            )}
          </div>

          {/* Back to Welcome button */}
          {onExit && (
            <button
              type="button"
              className="btn btn-welcome"
              onClick={onExit}
              title="Back to Welcome Hub"
            >
              ← Back to Welcome
            </button>
          )}

          {/* Close all / Exit buttons */}
          {openTraders.length > 0 && (
            <button
              type="button"
              className="btn btn-secondary"
              onClick={closeAll}
              title="Close all open trader panels"
            >
              Close All
            </button>
          )}

          {onExit && (
            <button
              type="button"
              className="btn btn-secondary"
              onClick={onExit}
              title="Exit workspace"
            >
              Exit
            </button>
          )}
        </div>
      </div>

      {/* Main workspace grid with touch support */}
      <div
        ref={gridRef}
        className={`traders-grid ${gridLayoutClass}`}
        onTouchStart={handleTouchStart}
        onTouchEnd={handleTouchEnd}
      >
        {openTraders.length === 0 ? (
          <div className="empty-state">
            <div className="empty-state-content">
              <h2>No traders open</h2>
              <p>Click "Open New Trader" to get started with Equity, FX, Bonds, or ETF trading.</p>
              <button
                type="button"
                className="btn btn-primary"
                onClick={() => setMenuOpen(true)}
              >
                Open First Trader
              </button>
            </div>
          </div>
        ) : (
          openTraders.map((trader, index) => (
            <TraderPanel
              key={trader.id}
              id={trader.id}
              type={trader.type}
              brokerClient={brokerClient}
              wsClient={wsClient}
              onClose={() => closeTrader(trader.id)}
              onDetach={handleDetachTrader}
              isDetached={detachedTraders.has(trader.id)}
              isMobileVisible={!isMobile || index === clampedIndex}
              onTitleClick={isMobile ? () => setShowTraderList(true) : undefined}
              onToast={showToast}
              onRegisterNavHandler={registerTraderNavHandler}
              onFocus={() => setCurrentTraderIndex(index)}
              isActive={currentTraderIndex === index}
            />
          ))
        )}
      </div>

      {/* Mobile navigation: back/next arrows at bottom */}
      {isMobile && openTraders.length > 1 && (
        <div className="trader-nav-arrows">
          <button
            type="button"
            className="trader-nav-btn"
            onClick={goToPrevTrader}
            disabled={clampedIndex === 0}
            aria-label="Previous trader"
            title="Previous trader"
          >
            &#10094;
          </button>
          <button
            type="button"
            className="trader-nav-btn"
            onClick={goToNextTrader}
            disabled={clampedIndex === openTraders.length - 1}
            aria-label="Next trader"
            title="Next trader"
          >
            &#10095;
          </button>
        </div>
      )}

      {/* Mobile trader list modal (tap title to see all traders) */}
      <div className={`trader-list-modal ${showTraderList ? 'active' : ''}`}>
        <div className="trader-list-modal-card">
          <div className="trader-list-modal-header">
            <h3>Traders ({openTraders.length})</h3>
            <button
              type="button"
              className="trader-list-modal-close"
              onClick={() => setShowTraderList(false)}
              aria-label="Close"
            >
              ✕
            </button>
          </div>
          <div className="trader-list-modal-body">
            {openTraders.map((trader, index) => (
              <button
                key={trader.id}
                type="button"
                className={`trader-list-item ${index === clampedIndex ? 'active' : ''}`}
                onClick={() => goToTrader(index)}
              >
                <span>
                  {getTraderTypeLabel(trader.type)}
                  <span className="trader-list-item-index">#{index + 1}</span>
                </span>
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Click outside modal to close */}
      {showTraderList && (
        <div
          className="trader-list-modal active"
          onClick={() => setShowTraderList(false)}
          style={{ pointerEvents: 'auto' }}
        />
      )}

      {/* Toast notification for detach/pop-out feedback */}
      {toastMessage && (
        <div className="trader-toast" role="status" aria-live="polite" aria-atomic="true">
          <span>{toastMessage}</span>
        </div>
      )}
    </div>
  );
}

export default MultiTraderWorkspace;

